import WebSocket from 'ws';
import { sendWsMessage } from '../connection/create-ws-bridge.js';
import {
  attachWebSocketClientPing,
  CLI_WEBSOCKET_CLIENT_PING_MS,
  logCliWebSocketError,
  safeCloseWebSocket,
} from '../connection/cli-ws-client.js';
import { handleInboundFirehoseFrame } from './handle-inbound-firehose-frame.js';
import type { FirehoseMessageDeps } from './routing/firehose-message-deps.js';
import type { FirehoseIdentifyParams } from './firehose-connection-types.js';
import type { LogFn } from '../log.js';
import type { PreviewEnvironmentManager } from '../preview-environments/manager/manager.js';

export interface WireFirehoseWebSocketOptions {
  ws: WebSocket;
  deps: FirehoseMessageDeps;
  log: LogFn;
  previewEnvironmentManager: PreviewEnvironmentManager;
  workspaceId: string;
  bridgeName: string;
  proxyPorts: number[];
  onOpen?: () => void;
  onClose?: (code: number, reason: string) => void;
  suppressWebSocketErrors?: () => boolean;
}

export interface WiredFirehoseWebSocket {
  disposeClientPing: () => void;
  sendIdentify: (params: FirehoseIdentifyParams) => void;
  detachPreviewEnvironmentManager: () => void;
}

export function wireFirehoseWebSocket(options: WireFirehoseWebSocketOptions): WiredFirehoseWebSocket {
  const {
    ws,
    deps,
    log,
    previewEnvironmentManager,
    workspaceId,
    bridgeName,
    proxyPorts,
    onOpen,
    onClose,
    suppressWebSocketErrors,
  } = options;

  let clearClientPing: (() => void) | null = null;
  let identifyParams: FirehoseIdentifyParams = { workspaceId, bridgeName, proxyPorts };

  function disposeClientPing(): void {
    clearClientPing?.();
    clearClientPing = null;
  }

  const firehoseSend = (payload: Record<string, unknown>) => {
    sendWsMessage(ws, payload);
  };

  function sendIdentify(params: FirehoseIdentifyParams): void {
    identifyParams = params;
    if (ws.readyState !== WebSocket.OPEN) return;
    sendWsMessage(ws, {
      type: 'identify',
      workspaceId: params.workspaceId,
      bridgeName: params.bridgeName,
      proxyPorts: params.proxyPorts,
    });
  }

  function detachPreviewEnvironmentManager(): void {
    previewEnvironmentManager.detachFirehose();
  }

  ws.on('open', () => {
    disposeClientPing();
    clearClientPing = attachWebSocketClientPing(ws, CLI_WEBSOCKET_CLIENT_PING_MS);
    onOpen?.();
    previewEnvironmentManager.attachFirehose(firehoseSend);
    sendIdentify(identifyParams);
  });

  ws.on('message', (raw: Buffer | string) => {
    handleInboundFirehoseFrame(raw, deps);
  });

  ws.on('close', (code, reason) => {
    disposeClientPing();
    detachPreviewEnvironmentManager();
    const reasonStr = typeof reason === 'string' ? reason : reason.toString();
    onClose?.(code, reasonStr);
  });

  ws.on('error', (err) => {
    disposeClientPing();
    if (!suppressWebSocketErrors?.()) {
      logCliWebSocketError(log, '[Proxy and log service]', err);
    }
    if (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN) {
      safeCloseWebSocket(ws);
    }
  });

  return { disposeClientPing, sendIdentify, detachPreviewEnvironmentManager };
}
